import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Navbar } from "@/components/Navbar";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accountType, setAccountType] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = location.state?.from?.pathname || "/developer-dashboard";
  const preselectedType = location.state?.preselectedType;

  useEffect(() => {
    if (preselectedType) {
      setAccountType(preselectedType);
      setIsSignUp(true);
    }
  }, [preselectedType]);

  const checkUserRole = async (userId: string) => {
    try {
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking user role:', error);
        return null;
      }

      return roles?.role || null;
    } catch (error) {
      console.error('Error in checkUserRole:', error);
      return null;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isForgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth`,
        });
        if (error) throw error;
        toast({
          title: "Success!",
          description: "Password reset email has been sent. Please check your inbox.",
        });
        setIsForgotPassword(false);
      } else if (isSignUp) {
        if (!accountType) {
          toast({
            title: "Error",
            description: "Please select an account type",
            variant: "destructive",
          });
          return;
        }

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              account_type: accountType,
            },
          },
        });
        if (error) throw error;
        toast({
          title: "Success!",
          description: "Please check your email to verify your account",
        });
        setIsSignUp(false);
        setEmail("");
        setPassword("");
        setShowPassword(false);
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        
        // Check if password is the default one
        if (password === "default12345") {
          navigate('/change-password');
          return;
        }

        // Check user's role
        const role = await checkUserRole(data.user.id);
        console.log('User role:', role);
        
        if (role === 'admin') {
          navigate('/admin');
        } else {
          // Check user's account type and redirect accordingly
          const accountType = data.user?.user_metadata?.account_type;
          if (accountType === 'company') {
            navigate('/company-dashboard');
          } else {
            navigate(from);
          }
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-24 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-md mx-auto space-y-6 p-6">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {isForgotPassword 
                ? "Reset your password"
                : isSignUp 
                  ? "Create an account" 
                  : "Sign in to your account"}
            </h2>
          </div>
          <form className="mt-8 space-y-1" onSubmit={handleAuth}>
            <div className="rounded-md shadow-sm space-y-4">
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {!isForgotPassword && (
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
              {isSignUp && (
                <Select
                  value={accountType}
                  onValueChange={(value) => setAccountType(value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="developer">Developer</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {!isSignUp && !isForgotPassword && (
              <Button
                variant="link"
                onClick={() => setIsForgotPassword(true)}
                className="text-sm"
              >
                Forgot your password?
              </Button>
            )}
            <div>
              <Button
                type="submit"
                className="w-full mt-4"
                disabled={isLoading}
              >
                {isLoading 
                  ? "Loading..." 
                  : isForgotPassword 
                    ? "Send reset instructions"
                    : isSignUp 
                      ? "Sign Up" 
                      : "Sign In"}
              </Button>
            </div>
          </form>

          <div className="text-center flex flex-col">
             <Button
              variant="link"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setIsForgotPassword(false);
                setAccountType(preselectedType || "");
              }}
              className="text-sm"
            >
              {isSignUp
                ? "Already have an account? Sign in"
                : "Don't have an account? Sign up"}
            </Button>
            {isForgotPassword && (
              <Button
                variant="link"
                onClick={() => setIsForgotPassword(false)}
                className="text-sm"
              >
                Back to sign in
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Auth;